import React, { useEffect, useState } from "react";
import {
  Interview,
  Project,
  calculateTotalCost,
} from "@/services/interviewService";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Plus, FileText, Trash2 } from "lucide-react";
import { formatCurrency } from "@/components/dashboard/interview-table/utils";
import AddExpenseModal from "./interview-table/AddExpenseModal";

interface ProjectOverviewProps {
  interviews: Interview[];
  currentProject: Project | null;
  recentlySyncedIds?: string[];
  onAddExpense?: (expenseData: Partial<Interview>) => void;
  onDeleteExpense?: (expenseId: string) => void;
  currentUserRole?: "owner" | "editor" | "viewer";
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({
  interviews,
  currentProject,
  recentlySyncedIds = [],
  onAddExpense,
  onDeleteExpense,
  currentUserRole = "viewer",
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [addExpenseModalOpen, setAddExpenseModalOpen] = useState(false);
  const [animatedProjectCompletion, setAnimatedProjectCompletion] = useState(0);
  const [animatedBudgetUtilization, setAnimatedBudgetUtilization] = useState(0);
  const [previousProjectCompletion, setPreviousProjectCompletion] = useState(0);
  const [previousBudgetUtilization, setPreviousBudgetUtilization] = useState(0);

  const targetInterviews = currentProject?.target_n || 15;
  const maxBudget = currentProject?.max_budget || 25000;

  // Count completed or scheduled interviews (follow-ups and secondary reports are automatically excluded)
  const completedOrScheduled = interviews.filter(
    (i) => i.status === "completed" || i.status === "scheduled"
  ).length;

  // Calculate project completion percentage
  const projectCompletionPercentage = Math.round(
    (completedOrScheduled / targetInterviews) * 100
  );

  // Calculate total cost (excluding canceled and in progress interviews, but including cancelled w/ fee and follow-ups)
  const totalCost = interviews.reduce((sum, interview) => {
    if (interview.status !== "canceled" && interview.status !== "in progress") {
      return sum + calculateTotalCost(interview);
    }
    return sum;
  }, 0);

  // Calculate budget utilization percentage
  const budgetUtilizationPercentage = Math.round((totalCost / maxBudget) * 100);
  const isBudgetExceeded = budgetUtilizationPercentage > 100;

  // Initialize animated values when component mounts or interviews change significantly
  useEffect(() => {
    if (recentlySyncedIds.length === 0) {
      // No new interviews, just set animated values to current values
      setAnimatedProjectCompletion(projectCompletionPercentage);
      setAnimatedBudgetUtilization(budgetUtilizationPercentage);
      setPreviousProjectCompletion(projectCompletionPercentage);
      setPreviousBudgetUtilization(budgetUtilizationPercentage);
    }
  }, [
    projectCompletionPercentage,
    budgetUtilizationPercentage,
    recentlySyncedIds.length,
  ]);

  // Trigger animation when new interviews are detected
  useEffect(() => {
    if (recentlySyncedIds.length > 0) {
      setIsAnimating(true);

      // Store the current animated values as the starting point
      const startProjectCompletion = animatedProjectCompletion;
      const startBudgetUtilization = animatedBudgetUtilization;

      // Target values
      const targetProjectCompletion = projectCompletionPercentage;
      const targetBudgetUtilization = budgetUtilizationPercentage;

      // Animation duration
      const duration = 2000; // 2 seconds
      const startTime = Date.now();

      const animateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
        const easedProgress = easeOutCubic(progress);

        // Interpolate values
        const currentProjectCompletion =
          startProjectCompletion +
          (targetProjectCompletion - startProjectCompletion) * easedProgress;
        const currentBudgetUtilization =
          startBudgetUtilization +
          (targetBudgetUtilization - startBudgetUtilization) * easedProgress;

        setAnimatedProjectCompletion(Math.round(currentProjectCompletion));
        setAnimatedBudgetUtilization(Math.round(currentBudgetUtilization));

        if (progress < 1) {
          requestAnimationFrame(animateProgress);
        } else {
          // Animation complete
          setIsAnimating(false);
          setPreviousProjectCompletion(targetProjectCompletion);
          setPreviousBudgetUtilization(targetBudgetUtilization);
        }
      };

      requestAnimationFrame(animateProgress);
    }
  }, [
    recentlySyncedIds,
    projectCompletionPercentage,
    budgetUtilizationPercentage,
  ]);

  // Animation classes for progress bars
  const animationClasses = isAnimating ? "progress-celebration" : "";
  const indicatorAnimationClasses = isAnimating
    ? "progress-indicator-celebration"
    : "";

  // Count interviews by network source (exclude follow-ups and secondary reports)
  const networkCounts = interviews.reduce((acc, interview) => {
    if (
      interview.status !== "canceled" &&
      interview.status !== "in progress" &&
      interview.status !== "follow-up" &&
      interview.status !== "follow-up scheduled" &&
      interview.status !== "follow-up completed" &&
      interview.status !== "secondary report"
    ) {
      const network = interview.network || "Unspecified";
      acc[network] = (acc[network] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  // Interview angle distribution (excluding cancelled w/ fee, follow-ups, and secondary reports)
  const interviewsByAngle = interviews.reduce((acc, interview) => {
    if (
      interview.status !== "canceled" &&
      interview.status !== "cancelled w/ fee" &&
      interview.status !== "in progress" &&
      interview.status !== "follow-up" &&
      interview.status !== "follow-up scheduled" &&
      interview.status !== "follow-up completed" &&
      interview.status !== "secondary report"
    ) {
      const angle = interview.angle || "Unspecified";
      if (!acc[angle]) {
        acc[angle] = { scheduled: 0, completed: 0, total: 0 };
      }
      if (interview.status === "scheduled") {
        acc[angle].scheduled += 1;
      } else if (interview.status === "completed") {
        acc[angle].completed += 1;
      }
      acc[angle].total += 1;
    }
    return acc;
  }, {} as Record<string, { scheduled: number; completed: number; total: number }>);

  // Get secondary reports
  const secondaryReports = interviews.filter(
    (i) => i.status === "secondary report"
  );

  // Calculate total secondary reports cost
  const secondaryReportsCost = secondaryReports.reduce(
    (sum, report) => sum + calculateTotalCost(report),
    0
  );

  // Calculate average interview cost (exclude canceled, cancelled w/ fee, in progress, follow-ups, and secondary reports - only count regular interviews)
  const activeInterviewsForAverage = interviews.filter(
    (i) =>
      i.status !== "canceled" &&
      i.status !== "cancelled w/ fee" &&
      i.status !== "in progress" &&
      i.status !== "follow-up" &&
      i.status !== "follow-up scheduled" &&
      i.status !== "follow-up completed" &&
      i.status !== "secondary report"
  );
  const totalCostForAverage = activeInterviewsForAverage.reduce(
    (sum, interview) => {
      return sum + calculateTotalCost(interview);
    },
    0
  );
  const averageInterviewCost =
    activeInterviewsForAverage.length > 0
      ? totalCostForAverage / activeInterviewsForAverage.length
      : 0;
  // Calculate remaining budget after secondary costs for accurate target cost per interview
  const remainingBudgetForInterviews = maxBudget - secondaryReportsCost;
  const targetInterviewCost = remainingBudgetForInterviews / targetInterviews;
  const costEfficiencyPercentage = targetInterviewCost
    ? Math.round((averageInterviewCost / targetInterviewCost) * 100)
    : 0;

  const handleAddExpense = () => {
    setAddExpenseModalOpen(true);
  };

  const handleExpenseSubmit = async (expenseData: Partial<Interview>) => {
    if (onAddExpense) {
      await onAddExpense(expenseData);
      setAddExpenseModalOpen(false);
    }
  };

  return (
    <Card className="mb-4 shadow-sm overflow-hidden">
      <CardContent className="p-3 bg-gradient-to-r from-white to-ivy-light/20 dark:from-gray-800 dark:to-gray-700/50 rounded-lg">
        <div className="grid grid-cols-12 gap-3">
          {/* Left column: Progress bars and Network Sources */}
          <div className="col-span-6">
            {/* Budget Utilization */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-sm font-medium">Budget Utilization</h3>
                <span
                  className={`text-sm font-medium ${
                    isBudgetExceeded ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {animatedBudgetUtilization}%
                </span>
              </div>
              <Progress
                value={Math.min(animatedBudgetUtilization, 100)}
                className={`h-2 mb-1 ${
                  isBudgetExceeded
                    ? "bg-red-100 dark:bg-red-900/50"
                    : animatedBudgetUtilization > 80
                    ? "bg-amber-100 dark:bg-amber-900/50"
                    : "bg-gray-100 dark:bg-gray-700"
                } ${animationClasses}`}
                indicatorClassName={`${
                  isBudgetExceeded
                    ? "bg-red-500"
                    : animatedBudgetUtilization > 80
                    ? "bg-amber-500"
                    : "bg-blue-500"
                } ${indicatorAnimationClasses}`}
              />
              <p className="text-xs text-muted-foreground">
                {formatCurrency(totalCost)} of {formatCurrency(maxBudget)}{" "}
                budget used
              </p>
            </div>

            {/* Project Completion */}
            <div className="mb-3">
              <div className="flex justify-between items-center mb-1">
                <h3 className="text-sm font-medium">Project Completion</h3>
                <span className="text-sm font-medium">
                  {animatedProjectCompletion}%
                </span>
              </div>
              <Progress
                value={animatedProjectCompletion}
                className={`h-2 mb-1 ${
                  animatedProjectCompletion >= 90
                    ? "bg-blue-100 dark:bg-blue-900/50"
                    : "bg-gray-100 dark:bg-gray-700"
                } ${animationClasses}`}
                indicatorClassName={`${
                  animatedProjectCompletion >= 90
                    ? "bg-blue-600"
                    : "bg-blue-500"
                } ${indicatorAnimationClasses}`}
              />
              <p className="text-xs text-muted-foreground">
                {completedOrScheduled} of {targetInterviews} target interviews
                completed or scheduled
              </p>
            </div>

            {/* Interview Cost */}
            <div className="justify-center">
              <h3 className="text-sm font-medium mb-1.5">Avg. Cost</h3>
              <div className="border rounded-md p-2 bg-slate-50 dark:bg-slate-800 dark:border-slate-600">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold">
                    {formatCurrency(averageInterviewCost)}
                  </div>
                  <div
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      costEfficiencyPercentage < 80
                        ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200"
                        : costEfficiencyPercentage > 100
                        ? "bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-200"
                        : "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200"
                    }`}
                  >
                    {costEfficiencyPercentage}% of target interview cost
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  <span
                    className={
                      costEfficiencyPercentage < 80
                        ? "text-green-600 dark:text-green-400"
                        : costEfficiencyPercentage > 100
                        ? "text-red-600 dark:text-red-400"
                        : "text-blue-600 dark:text-blue-400"
                    }
                  >
                    {costEfficiencyPercentage < 80
                      ? "Under"
                      : costEfficiencyPercentage > 100
                      ? "Over"
                      : "On"}{" "}
                    budget
                  </span>{" "}
                  ({formatCurrency(targetInterviewCost)} / interview goal)
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Stats Grid and Distribution Table */}
          <div className="col-span-6">
            {/* Interview Angle Distribution */}
            <div>
              <div className="border rounded-md dark:border-gray-600">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
                      <th className="text-left p-1.5 font-medium text-gray-500 dark:text-gray-300">
                        Angle
                      </th>
                      <th className="text-center p-1.5 font-medium text-gray-500 dark:text-gray-300">
                        Scheduled
                      </th>
                      <th className="text-center p-1.5 font-medium text-gray-500 dark:text-gray-300">
                        Completed
                      </th>
                      <th className="text-center p-1.5 font-medium text-gray-500 dark:text-gray-300">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(interviewsByAngle).map(
                      ([angle, counts], index) => (
                        <tr
                          key={angle}
                          className={
                            index !== Object.keys(interviewsByAngle).length - 1
                              ? "border-b dark:border-gray-600"
                              : ""
                          }
                        >
                          <td className="p-1.5">{angle}</td>
                          <td className="text-center p-1.5">
                            {counts.scheduled}
                          </td>
                          <td className="text-center p-1.5">
                            {counts.completed}
                          </td>
                          <td className="text-center p-1.5">{counts.total}</td>
                        </tr>
                      )
                    )}
                    <tr className="border-t bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                      <td className="p-1.5 font-medium">Total</td>
                      <td className="text-center p-1.5 font-medium">
                        {Object.values(interviewsByAngle).reduce(
                          (sum, counts) => sum + counts.scheduled,
                          0
                        )}
                      </td>
                      <td className="text-center p-1.5 font-medium">
                        {Object.values(interviewsByAngle).reduce(
                          (sum, counts) => sum + counts.completed,
                          0
                        )}
                      </td>
                      <td className="text-center p-1.5 font-medium">
                        {Object.values(interviewsByAngle).reduce(
                          (sum, counts) => sum + counts.total,
                          0
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-3 ml-2">
              {/* Expert Network Sources */}
              <div>
                <h3 className="text-sm font-medium mb-1.5">
                  Expert Network Sources
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(networkCounts).map(
                    ([network, count], index) => {
                      // Array of background/text color pairs for different networks
                      const colorClasses = [
                        "bg-blue-50 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200",
                        "bg-green-50 dark:bg-green-900/50 text-green-800 dark:text-green-200",
                        "bg-purple-50 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200",
                        "bg-amber-50 dark:bg-amber-900/50 text-amber-800 dark:text-amber-200",
                        "bg-rose-50 dark:bg-rose-900/50 text-rose-800 dark:text-rose-200",
                        "bg-indigo-50 dark:bg-indigo-900/50 text-indigo-800 dark:text-indigo-200",
                        "bg-teal-50 dark:bg-teal-900/50 text-teal-800 dark:text-teal-200",
                        "bg-orange-50 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200",
                      ];

                      // Use modulo to cycle through colors if there are more networks than colors
                      const colorClass =
                        colorClasses[index % colorClasses.length];

                      return (
                        <div
                          key={network}
                          className={`px-2 py-0.5 rounded-md text-xs ${colorClass}`}
                        >
                          {network}: {count}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Reports Section */}
        <div className="col-span-12 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Secondary Reports & Research
              {secondaryReports.length > 0 && (
                <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                  ({secondaryReports.length}{" "}
                  {secondaryReports.length === 1 ? "report" : "reports"} •{" "}
                  {formatCurrency(secondaryReportsCost)})
                </span>
              )}
            </h3>
            {currentUserRole !== "viewer" && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddExpense}
                className="h-8 gap-1 bg-white dark:bg-gray-800 border-teal-300 dark:border-teal-700 text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:border-teal-400 dark:hover:border-teal-600 hover:text-teal-800 dark:hover:text-teal-200 font-normal transition-all"
              >
                <Plus className="h-3 w-3" />
                <span>Add Secondary Report</span>
              </Button>
            )}
          </div>

          {secondaryReports.length > 0 && (
            <div className="space-y-1.5">
              {secondaryReports.map((report) => (
                <div
                  key={report.id}
                  className="flex items-center justify-between p-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-md"
                >
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {report.title || "Unnamed Report"}
                      </span>
                      {report.company &&
                        report.company !== "Unknown Provider" && (
                          <>
                            <span className="text-gray-400 dark:text-gray-500">
                              •
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {report.company}
                            </span>
                          </>
                        )}
                      {report.angle && (
                        <>
                          <span className="text-gray-400 dark:text-gray-500">
                            •
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 italic truncate">
                            {report.angle}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    <span className="text-sm font-semibold text-teal-700 dark:text-teal-300">
                      {formatCurrency(calculateTotalCost(report))}
                    </span>
                    {currentUserRole !== "viewer" && onDeleteExpense && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteExpense(report.id)}
                        className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Add Expense Modal */}
      <AddExpenseModal
        isOpen={addExpenseModalOpen}
        onClose={() => setAddExpenseModalOpen(false)}
        onSubmit={handleExpenseSubmit}
        projectId={currentProject?.id}
      />
    </Card>
  );
};

export default ProjectOverview;
